/*!
  Pioneer WFRIA Panel v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Panel"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Panel") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Panel");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["animated", "animationClass", "animationTimeout", "bodyRole", "documentTitle", "children", "className", "customStyles", "onClose", "open", "title", "footer", "trigger", "height", "width", "fullScreen", "headerDock", "headerDockAriaLabel", "customStrings", "aria-label", "elementToFocusOnClose", "elementToFocusOnOpen", "noCloseButton"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useUniqueId } from "../common/hooks";
import { invalidIf, requiredIf } from "../common/proptypes";
import { mergeRefs, safeMerge } from "../common/utilities";
import { BackgroundImage, Popup } from "../private";
import { useResizeObserver } from "../private/ThirdParty";
import { ThemeProviderContext } from "../ThemeProvider/ThemeProviderContext";
import extractTheme from "./extractTheme";
import { PanelContext } from "./PanelContext";
import STRINGS from "./strings";
import { Button, Flexgrid, FlexgridItem, IconClose } from "..";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const CSSTransition = /*#__PURE__*/React.lazy(() => import("react-transition-group/CSSTransition"));
const MINIMUM_BODY_HEIGHT = 200;

function Header(_ref) {
  let {
    cssHeader,
    cssHeaderInner,
    id,
    title,
    onClose,
    closeTooltipContent,
    fullScreen,
    noCloseButton
  } = _ref;

  if (noCloseButton && fullScreen) {
    return _jsx("div", {
      css: cssHeader,
      children: _jsx("div", {
        css: cssHeaderInner,
        children: _jsx("h1", {
          id: id,
          children: title
        })
      })
    });
  }
  /**
   * NOTE: the close button, while visually on the right side
   * of the Panel header, has to be the first element in the DOM
   * for accessibility reasons. We are using flexbox order to swap
   * them visually.
   */


  return _jsx("div", {
    css: cssHeader,
    children: _jsxs(Flexgrid, {
      css: cssHeaderInner,
      "data-testid": "panel-header",
      direction: "row-reverse",
      alignHorizontal: "space-between",
      alignVertical: "center",
      columnGap: 0,
      children: [_jsx(FlexgridItem, {
        children: _jsx(Button, {
          "aria-label": closeTooltipContent,
          kind: fullScreen ? "standard" : "chromeless",
          onClick: onClose,
          centerIcon: _jsx(IconClose, {
            size: "xsmall",
            "aria-hidden": "true"
          })
        })
      }), _jsx(FlexgridItem, {
        children: _jsx("h1", {
          id: id,
          children: title
        })
      })]
    })
  });
}

const HeaderDock = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    cssHeaderDock,
    headerDockAriaLabel,
    headerDock
  } = _ref2;
  return _jsx("div", {
    ref: ref,
    children: _jsx("section", {
      css: cssHeaderDock,
      className: "pnr-panel__header-dock",
      "aria-label": headerDockAriaLabel,
      children: headerDock
    })
  });
});
/**
 * Panels are used to support the display of supplementary
 * information, for offering selections from larger data sets,
 * and/or for displaying auxiliary tasks in support of
 * more complex workflows.
 */

const Panel = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    animated,
    animationClass,
    animationTimeout,
    bodyRole,
    documentTitle,
    children,
    className,
    customStyles,
    onClose,
    open,
    title,
    footer,
    trigger,
    height,
    width,
    fullScreen,
    headerDock,
    headerDockAriaLabel,
    customStrings,
    "aria-label": ariaLabel,
    elementToFocusOnClose,
    elementToFocusOnOpen,
    noCloseButton
  } = _ref3,
      otherProps = _objectWithoutProperties(_ref3, _excluded);

  const titleId = useUniqueId("pnr-panel-title-");
  const [fixedHeaderDock, setHeaderDockFixed] = useState(true);
  const [tabIndex, setTabIndex] = useState(null);
  const originalTitleRef = useRef();
  const panelBodyRef = useRef();
  const {
    ref: internalPanelRef,
    height: panelBodyHeight
  } = useResizeObserver();
  const {
    ref: headerDockRef,
    height: headerDockHeight
  } = useResizeObserver();
  const mergedPanelBodyRef = mergeRefs([internalPanelRef, panelBodyRef]);
  const theme = useTheme();
  const context = useContext(ThemeProviderContext) || {};
  const {
    cssOverlay,
    cssRoot,
    cssWrapper,
    cssHeader,
    cssHeaderInner,
    cssHeaderDock,
    cssBody,
    cssBodyContent,
    cssFooter
  } = extractTheme({
    fullScreen,
    height,
    width,
    theme
  });

  const {
    close,
    dialog,
    closeButtonAriaLabel
  } = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const closeTooltipContent = closeButtonAriaLabel || `${close} ${title ? title : ""} ${dialog}`;
  /**
   * If the panel is opened, set the document title to
   * the panel's title and documentTitle . If it's closed, restore the original
   * document.title.
   */

  useEffect(() => {
    let docTitle = "";

    if (open) {
      if (title && documentTitle) {
        docTitle = `${title} | ${documentTitle}`;
      } else if (title) {
        docTitle = title;
      } else if (documentTitle) {
        docTitle = documentTitle;
      }

      originalTitleRef.current = document.title;
      document.title = docTitle;
    }

    return () => {
      if (open) {
        document.title = originalTitleRef.current;
      }
    };
  }, [title, documentTitle, open]);
  /**
   * If the body of the panel is scrolling, accessibility requires
   * a tabIndex and therefore a visible focus border.
   */

  useEffect(() => {
    if (open && panelBodyHeight) {
      const panelBodyElement = panelBodyRef.current || {};
      const panelBodyScrollHeight = panelBodyElement.scrollHeight;
      /* istanbul ignore next */

      if (typeof panelBodyScrollHeight !== "undefined") {
        setTabIndex(panelBodyHeight !== panelBodyScrollHeight ? "0" : null);
      }
    }
  }, [open, panelBodyHeight, panelBodyRef]);
  /**
   * If there is a headerDock, we need to move it around to either make it
   * fixed (sticky) or not:
   *   - if the body content area height is higher than MINIMUM_BODY_HEIGHT
   *      -> headerDock needs to be sticky, so we move it between the
   *         panel header and the panel body (which is scrollable).
   *   - if the body content area height is lower than MINIMUM_BODY_HEIGHT
   *      -> headerDock needs to be scrolling, so we move it inside the
   *         panel body (which is scrollable).
   */

  useEffect(() => {
    if (open && headerDock) {
      if (fixedHeaderDock) {
        /**
         * Header dock is fixed, so we need to take its height into
         * account to calculate the remaining body size.
         */
        if (panelBodyHeight + headerDockHeight < MINIMUM_BODY_HEIGHT) {
          setHeaderDockFixed(false);
        }
      } else if (!fixedHeaderDock && panelBodyHeight >= MINIMUM_BODY_HEIGHT) {
        /**
         * Header dock is NOT fixed, so we only need to take the
         * body size into account.
         */
        setHeaderDockFixed(true);
      }
    }
  }, [open, panelBodyHeight, fixedHeaderDock, headerDock, headerDockHeight]);
  /**
   * Only calls onClose if the event was caused by a click of the Panel
   * close button or press of the 'Esc' key. This prevents `onClose` from
   * being called on change of the open prop.
   * @param {EventFacade} e The click or keyDown event facade.
   */

  const onPanelClose = e => {
    if (e.target) {
      onClose && onClose();
    }
  };

  const wrapperClassNames = clsx(className, context.scopedClassName);
  const overlayStyles = safeMerge(cssOverlay, customStyles === null || customStyles === void 0 ? void 0 : customStyles.overlay);
  const rootStyles = safeMerge(cssRoot, customStyles === null || customStyles === void 0 ? void 0 : customStyles.root);
  const wrapperStyles = safeMerge(cssWrapper, customStyles === null || customStyles === void 0 ? void 0 : customStyles.wrapper);
  const headerStyles = safeMerge(cssHeader, customStyles === null || customStyles === void 0 ? void 0 : customStyles.header);
  const headerDockStyles = safeMerge(cssHeaderDock, customStyles === null || customStyles === void 0 ? void 0 : customStyles.headerDock);
  const bodyStyles = safeMerge(cssBody, customStyles === null || customStyles === void 0 ? void 0 : customStyles.body);
  const bodyContentStyles = safeMerge(cssBodyContent, customStyles === null || customStyles === void 0 ? void 0 : customStyles.bodyContent);
  /**
   * getPanelContextType
   *
   * Use this function to update the PanelContext value's `type` attribute.
   *
   * PanelContext types: "modal" | "fullScreen".
   *
   * @returns A string representation of the Panel type.
   */

  const getPanelContextType = () => {
    if (fullScreen) {
      return "fullScreen";
    } // The default context is "modal".


    return "modal";
  };

  const panelElement = _jsx(PanelContext.Provider, {
    value: {
      type: getPanelContextType()
    },
    children: _jsx(Popup, _objectSpread(_objectSpread({
      modal: true,
      lockScroll: true,
      nested: true,
      closeOnEscape: true,
      animated: animated,
      animationTimeout: animationTimeout,
      open: open,
      onClose: onPanelClose,
      overlayStyle: overlayStyles,
      contentStyle: rootStyles,
      "aria-labelledby": title ? titleId : null,
      "aria-label": title ? null : ariaLabel,
      elementToFocusOnClose: elementToFocusOnClose ? elementToFocusOnClose : null,
      elementToFocusOnOpen: elementToFocusOnOpen,
      type: "panel",
      ariaModal: true
    }, otherProps), {}, {
      children: _jsxs("div", {
        css: wrapperStyles,
        ref: ref,
        className: wrapperClassNames,
        children: [_jsx(Header, {
          noCloseButton: noCloseButton,
          fullScreen: fullScreen,
          cssHeader: headerStyles,
          cssHeaderInner: cssHeaderInner,
          id: titleId,
          title: title,
          onClose: onClose,
          closeTooltipContent: closeTooltipContent
        }), fullScreen && _jsx(BackgroundImage, {}), fixedHeaderDock && headerDock && _jsx(HeaderDock, {
          ref: headerDockRef,
          cssHeaderDock: headerDockStyles,
          headerDockAriaLabel: headerDockAriaLabel,
          headerDock: headerDock
        }), _jsxs("div", {
          tabIndex: tabIndex,
          ref: mergedPanelBodyRef,
          "data-testid": "panel-body",
          className: "pnr-panel__body",
          css: bodyStyles,
          role: bodyRole,
          children: [!fixedHeaderDock && headerDock && _jsx("div", {
            ref: headerDockRef,
            children: _jsx(HeaderDock, {
              cssHeaderDock: headerDockStyles,
              headerDockAriaLabel: headerDockAriaLabel,
              headerDock: headerDock
            })
          }), _jsx("div", {
            className: "pnr-panel__body-content",
            "data-testid": "panel-body-content",
            css: bodyContentStyles,
            children: children
          })]
        }), footer && _jsxs("div", {
          css: cssFooter,
          children: [_jsx("div", {
            children: footer
          }), " "]
        })]
      })
    }))
  });

  return _jsxs(React.Fragment, {
    children: [trigger ? trigger : null, animated ? _jsx(React.Suspense, {
      fallback: _jsx("div", {}),
      children: _jsx(CSSTransition, {
        in: open,
        timeout: animationTimeout,
        mountOnEnter: true,
        unmountOnExit: true,
        classNames: animationClass,
        children: panelElement
      })
    }) : panelElement]
  });
});
Panel.propTypes = process.env.NODE_ENV !== "production" ? {
  /** This is what will be displayed inside the Panel. */
  children: PropTypes.node.isRequired,

  /** Adds animation to the opening and closing states of the Panel. */
  animated: PropTypes.bool,

  /** Adds animation styles using this class. */
  animationClass: PropTypes.string,

  /** Animation timeout. */
  animationTimeout: PropTypes.number,

  /**
   * aria-label for the Panel when `title` prop
   * is not configured.
   */
  "aria-label": requiredIf(PropTypes.string, (props, propName) => !props.title && !props[propName], "if `title` is not configured"),

  /**
   * Allow the passing of a specific role to the body of the Panel.
   * See ListBuilder for example.
   */
  bodyRole: PropTypes.string,
  className: PropTypes.string,

  /**
   * Override for strings used within the component.
   *  - `closeButtonAriaLabel`: aria-label for the Panel's
   *     close button (this is what is displayed in the
   *     Button's tooltip).
   *     Default to "close <title> dialog".
   */
  customStrings: PropTypes.shape({
    closeButtonAriaLabel: PropTypes.string
  }),

  /**
   * customStyles accepts object, that has the same signature as the Panel theme
   * (root, overlay, header, etc.)
   * allowing any consuming component that needs to use Panel, will override panel styles
   * used within the component.
   */
  customStyles: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /**
   * This prop value will be added to the browser title when the Panel is open.
   * When the title and documentTitle  props are provided to the Panel,
   * then the browser title will consist of the title followed by the documentTitle .
   */
  documentTitle: PropTypes.string,

  /**
   * This prop identifies the element that should receive
   * focus after Panel is closed.
   * It can be a css selector or a function which
   * returns a DOM node.
   *
   * This prop should only be used in rare cases (when for example,
   * the triggering element that opened the Panel is going to be
   * gone once the Panel closes.) In the majority of cases, the
   * Panel will automatically move the focus back to the element
   * that was used to trigger it.
   */
  elementToFocusOnClose: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * This prop identifies the element that should receive focus when
   * the Panel is opened.
   * It can be a css selector or a function which returns a DOM node.
   */
  elementToFocusOnOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** The footer of the Panel. */
  footer: PropTypes.node,

  /** If true, the Panel will take the full screen. */
  fullScreen: PropTypes.bool,

  /**
   * Sticky container located between the title and the
   * body of the Panel.
   */
  headerDock: PropTypes.node,

  /** aria-label for header dock container. */
  headerDockAriaLabel: PropTypes.string,

  /**
   * The height of the Panel at desktop breakpoint.
   * Must include the unit type. i.e. 200px or 40%.
   */
  height: PropTypes.string,

  /**
   * Remove the close button - only valid if fullScreen is true.
   */
  noCloseButton: invalidIf(PropTypes.bool, (props, propName) => {
    return props[propName] && !props.fullScreen;
  }, "when `fullScreen` prop is not set to `true`."),

  /**
   * Function that will be executed after the close button
   * is pressed.
   */
  onClose: PropTypes.func,

  /** Shows or hides the Panel. */
  open: PropTypes.bool,

  /** The title of the Panel. */
  title: PropTypes.string,
  trigger: PropTypes.node,

  /**
   * The width of the Panel at desktop breakpoint.
   * Must include the unit type. i.e. 200px or 40%.
   *
   * **Note**: Regardless of the value entered,
   * the calculated width will always be lower than 80% of
   * viewport width, and greater than 430px.
   */
  width: PropTypes.string
} : {};
Panel.defaultProps = {
  animated: false,
  animationClass: "pnr-panel-animation",
  animationTimeout: 0,
  "aria-label": null,
  className: null,
  documentTitle: null,
  footer: null,
  fullScreen: false,
  headerDock: null,
  headerDockAriaLabel: null,
  height: null,
  onClose: null,
  open: false,
  width: null,
  title: null,
  trigger: null,
  elementToFocusOnClose: null,
  elementToFocusOnOpen: null,
  customStrings: null,
  customStyles: null,
  noCloseButton: false,
  bodyRole: null
};
Panel.displayName = "Panel";
export default Panel;